// @ts-ignore - no type definitions available for troika-three-text
import { Text } from 'troika-three-text'
import * as THREE from 'three'
import MathUtils from "./utilities/MathUtils";
import Scenario from './scenarioElements/Scenario';
import TextVariables from './TextVariables';
import TextUtils from './utilities/TextUtils';
import Engine from './Engine';
import { CustomMaterials } from './CustomMaterials';
import Callout from './Callout';
import HighlightSphere from './HighlightSphere';
import TransformUtils from "./utilities/TransformUtils";
import SceneObjectUtils from './utilities/SceneObjectUtils';
import { ContentAnchors, CustomEventTypes, ValueTypes } from '../globals';
import { IText3D } from '../interfaces/Text3D';
import { SupportedRichTextTags } from '../interfaces/Misc';
import { VisibilityModes } from '../interfaces/enums';
const tempColor = new THREE.Color();

interface ITroikaText extends THREE.Mesh {
	text: string;
	anchorX: TroikaXAnchor;
	anchorY: TroikaYAnchor;
	fontSize: number;
	color: string;
	fillOpacity: number;
	colorRanges: Record<number, number> | null;
	font: string;
	sync(callback?: () => void): void;
	dispose(): void;
}

enum TroikaXAnchor {
	Left = "left",
	Center = "center",
	Right = "right"
}

enum TroikaYAnchor {
	Top = "top",
	TopBaseline = "top-baseline",
	TopCap = "top-cap",
	Middle = "middle",
	Bottom = "bottom",
	BottomBaseline = "bottom-baseline",
	BottomCap = "bottom-cap"
}

class TroikaText3D extends THREE.Mesh
{
	static fontBlob: string;
	static allTexts: TroikaText3D[] = [];
	static textGeometries = new Map<string, ITroikaText>();
	static textsBank = new Map<string, TroikaText3D>();

	troikaText!: ITroikaText;
	modifiedText: string;


	private _hidden: boolean = false;
	private _fadeOpacity: number = 1;
	get hidden(): boolean
	{
		return this._hidden;
	}
	set hidden(value: boolean)
	{
		this._hidden = value;
		this.updateOpacity();
	}
	get fadeOpacity(): number
	{
		return this._hidden ? 0 : this._fadeOpacity;
	}
	set fadeOpacity(value: number)
	{
		this._fadeOpacity = value;
		this.updateOpacity();
	}
	updateOpacity()
	{
		this.troikaText.fillOpacity = this._hidden ? 0 : this._fadeOpacity;
	}
	constructor()
	{
		super(new THREE.BufferGeometry(), CustomMaterials.getModelRootMaterial());
		// @ts-ignore - no type definitions available for troika-three-text
		this.troikaText = new Text();
		this.troikaText.fontSize = .3;
		this.troikaText.userData.hideInHierarchy = true;
		this.name = "Text3D";
		this.troikaText.font = TroikaText3D.fontBlob;
		const text3d : IText3D = {
			text: "text",
			color: "0xffffff",
			anchor: ContentAnchors.MiddleCenter,
			isRichText: false,
		};
		this.userData.text3d = text3d;
		this.modifiedText = this.userData.text3d.text;
		this.troikaText.userData.ignoreExport = true;
		this.troikaText.userData.requiresOutline = true;
		this.add(this.troikaText);
		this.applyAnchor();
		this.generateTextGeometry();
		TroikaText3D.allTexts.push(this);

		// Bind methods to preserve 'this' context for event listeners and callbacks
		this.generateTextGeometry = this.generateTextGeometry.bind(this);
		this.handleUpdateEvents = this.handleUpdateEvents.bind(this);
		this.handleObjectPropertyUpdate = this.handleObjectPropertyUpdate.bind(this);
		this.onColorChangeEffect = this.onColorChangeEffect.bind(this);
		this.onFadeInEffect = this.onFadeInEffect.bind(this);

		document.addEventListener(CustomEventTypes.onFontLoaded, this.generateTextGeometry);
		document.addEventListener(CustomEventTypes.onElementNameChanged, this.handleUpdateEvents);
		document.addEventListener(CustomEventTypes.objectNameChanged, this.handleObjectPropertyUpdate);
		document.addEventListener(CustomEventTypes.onIOTValueUpdated, this.handleUpdateEvents);
	}

	onDispose() : void
	{
		document.removeEventListener(CustomEventTypes.onFontLoaded, this.generateTextGeometry);
		document.removeEventListener(CustomEventTypes.onElementNameChanged, this.handleUpdateEvents);
		document.removeEventListener(CustomEventTypes.objectNameChanged, this.handleObjectPropertyUpdate);
		document.removeEventListener(CustomEventTypes.onIOTValueUpdated, this.handleUpdateEvents);
	}

	onSetVisibilityMode(visibilityMode : VisibilityModes) : void
	{
		switch (visibilityMode)
		{
			case VisibilityModes.Hidden:
				this.hidden = true;
				break;
			case VisibilityModes.Ghosted:
			case VisibilityModes.Solid:
			case VisibilityModes.Custom:
				this.hidden = false;
				break;
		}
		if (this.userData.fadeInFactor !== undefined) SceneObjectUtils.applyFadeInEffect(this);
	}

	handleUpdateEvents() : void
	{
		this.setText(this.getText());
	}

	handleObjectPropertyUpdate(args : CustomEventInit): void
	{
		if(args?.detail?.objects)
		{
			for(const object of args.detail.objects)
			{
				if(object.userData.guid === this.userData.guid)
				{
					this.setText(this.getText());
					break;
				}
			}
		}
	}

	static clearUnusedGeometries()
	{
		console.warn("This isn't needed for TroikaText3D: clearUnusedGeometries");
	}

	static clearData()
	{
		for (const text of TroikaText3D.textGeometries.values())
		{
			text.dispose();
		}
		TroikaText3D.textGeometries.clear();
		TroikaText3D.textsBank.clear();
	}

	static getText3d(objectGuid : string)
	{
		return TroikaText3D.textsBank.get(objectGuid);
	}

	static addToText3dBank(objectGuid : string, text3d: TroikaText3D)
	{
		TroikaText3D.textsBank.set(objectGuid, text3d);
	}

	static async loadFont()
	{
		if (TroikaText3D.fontBlob) return;

		// jest hates urls that aren't full
		if (process.env.JEST_WORKER_ID)
		{
			TroikaText3D.fontBlob = './static/fonts/Roboto+NotoCJKSC.woff';
			document.dispatchEvent(new CustomEvent(CustomEventTypes.onFontLoaded));
			return;
		}
		fetch('./static/fonts/Roboto+NotoCJKSC.woff')
			.then(res => res.arrayBuffer())
			.then(arrayBuffer => 
			{
				const blob = new Blob([arrayBuffer], { type: 'font/woff' });
				const blobUrl = URL.createObjectURL(blob);
				TroikaText3D.fontBlob = blobUrl;
				document.dispatchEvent(new CustomEvent(CustomEventTypes.onFontLoaded));
			});

		return;
	}

	getText()
	{
		return this.userData.text3d.text;
	}

	setText(value: string)
	{
		const currentStep = Scenario.currentStep();
		if (currentStep?.id)
		{
			this.modifiedText = TextVariables.textToData(value, { partName: this.name, stepId: currentStep.id, sequenceId: Scenario.currentSequence()?.id, metaData: this.userData?.metaData});
		}
		else
		{
			this.modifiedText = "";
		}

		this.userData.text3d.text = value;
		this.userData.text3d.isRichText = TextUtils.isRichTextTMP(value);
		this.generateTextGeometry();
	}

	getTextColor() : string
	{
		return this.userData.text3d.color.substring(2);
	}

	setTextColor(value: string)
	{
		if (value.length >= 6)
		{
			value = value.substr(value.length - 6);
		}
		else
		{
			const zerosArray = new Array(6 - value.length).fill("0");
			value = value + zerosArray.join('');
		}
			
		this.userData.text3d.color = '0x' + value;
		this.troikaText.color = '#' + value;
	}

	getTextAnchor(): ContentAnchors
	{
		return this.userData.text3d.anchor;
	}

	setTextAnchor(value: ContentAnchors)
	{
		this.userData.text3d.anchor = value;
		this.applyAnchor();
	}

	generateTextGeometry()
	{
		if (!this.modifiedText && this.modifiedText !== '') this.setText(this.getText());

		this.troikaText.text = this.handleTags(this.modifiedText);
		// COLOR RANGE: uncomment this for color range stuff
		// this.troikaText.colorRanges = this.colorRange;
		this.troikaText.sync(() => 
		{
			if (this.geometry) this.geometry.dispose();
			if (this.troikaText.geometry?.boundingBox)
			{
				this.geometry = new THREE.BoxGeometry(
					this.troikaText.geometry.boundingBox.max.x - this.troikaText.geometry.boundingBox.min.x, 
					this.troikaText.geometry.boundingBox.max.y - this.troikaText.geometry.boundingBox.min.y, 
					0.025
				);
			}
			else
			{
				this.geometry = new THREE.BoxGeometry(0.025, 0.025, 0.025);
			}
			const isSelected = Engine.getInstance().getPickingManager().isObjectSelected(this);
			if (isSelected) document.dispatchEvent(new CustomEvent(CustomEventTypes.objectSelectionChanged)); //cause the outline renderer to refresh
		});
	}

	applyAnchor()
	{
		switch (this.userData.text3d.anchor as ContentAnchors)
		{
			case ContentAnchors.UpperLeft:
				this.troikaText.anchorX = TroikaXAnchor.Left;
				this.troikaText.anchorY = TroikaYAnchor.Top;
				break;
			case ContentAnchors.UpperCenter:
				this.troikaText.anchorX = TroikaXAnchor.Center;
				this.troikaText.anchorY = TroikaYAnchor.Top;
				break;
			case ContentAnchors.UpperRight:
				this.troikaText.anchorX = TroikaXAnchor.Right;
				this.troikaText.anchorY = TroikaYAnchor.Top;
				break;
			case ContentAnchors.MiddleLeft:
				this.troikaText.anchorX = TroikaXAnchor.Left;
				this.troikaText.anchorY = TroikaYAnchor.Middle;
				break;
			case ContentAnchors.MiddleCenter:
				this.troikaText.anchorX = TroikaXAnchor.Center;
				this.troikaText.anchorY = TroikaYAnchor.Middle;
				break;
			case ContentAnchors.MiddleRight:
				this.troikaText.anchorX = TroikaXAnchor.Right;
				this.troikaText.anchorY = TroikaYAnchor.Middle;
				break;
			case ContentAnchors.LowerLeft:
				this.troikaText.anchorX = TroikaXAnchor.Left;
				this.troikaText.anchorY = TroikaYAnchor.Bottom;
				break;
			case ContentAnchors.LowerCenter:
				this.troikaText.anchorX = TroikaXAnchor.Center;
				this.troikaText.anchorY = TroikaYAnchor.Bottom;
				break;
			case ContentAnchors.LowerRight:
				this.troikaText.anchorX = TroikaXAnchor.Right;
				this.troikaText.anchorY = TroikaYAnchor.Bottom;
				break;
		}
		this.generateTextGeometry();
	}

	offsetExtrudedText()
	{
		//0.00625 is the half-depth of the bounding box of our previously extruded text. (0.25 geometry scale * 0.05 text height / 2.0)
		//this offsets the now flat text to be aligned with the front edge of the extruded text's bounding box
		const offset = 0.00625 * this.scale.z;
		const worldQuat = new THREE.Quaternion();
		this.getWorldQuaternion(worldQuat);
		TransformUtils.translateWorldSpace(this, new THREE.Vector3(0, 0, offset).applyQuaternion(worldQuat));

		for (const child of this.children)
		{
			if (!child.userData.hideInHierarchy)
			{
				TransformUtils.translateWorldSpace(child, new THREE.Vector3(0, 0, -offset).applyQuaternion(worldQuat));
			}
		}
	}

	copy(source : TroikaText3D, recursive = true)
	{
		THREE.Object3D.prototype.copy.call(this, source, false);
	
		this.setText(source.userData.text3d.text);
		this.generateTextGeometry();
		this.setTextAnchor(source.userData.text3d.anchor);
		this.setTextColor(source.userData.text3d.color);
	
		if (recursive === true)
		{
			for (let i = 0; i < source.children.length; i++)
			{
				const child = source.children[i];
				if (!child.userData.hideInHierarchy || child instanceof Callout || child instanceof HighlightSphere)
				{
					this.add(child.clone());
				}
			}
		}
	
		return this;
	}
	handleTags(text: string)
	{
		// Swap the comments on these for color range stuff
		// const handledTags = ['color']
		const handledTags = new Array<string>();
		const tagPattern = new RegExp(
			`<\\/?(?:${SupportedRichTextTags.filter(tag => !handledTags.includes(tag)).join('|')})(?:=[^>]+)?\\s*>`,
			'gi'
		); //thanks chatgpt
		const unsupportedTags = text.match(tagPattern);
		text = text.replace(tagPattern, '');
		if (!unsupportedTags)
		{
			this.userData.text3d.unsupportedTags = undefined;
		}
		else
		{
			const tagSet = new Set<string>();
			for (const unSupportedTag of unsupportedTags)
			{
				SupportedRichTextTags.forEach(tag => 
				{
					if (unSupportedTag.includes(tag))
					{
						tagSet.add(tag);
					}
				});
			}
			this.userData.text3d.unsupportedTags = Array.from(tagSet);
		}
		// TODO: COLOR RANGE: uncomment this for color range stuff
		// text = this.stripColorsAndGetIndexes(text);
		return text;
	}

	onColorChangeEffect(factor : number)
	{
		tempColor.setHex(this.userData.text3d.color);
		this.troikaText.color = '#' + (MathUtils.lerpValue(tempColor, this.userData.overrideColor, factor, ValueTypes.Color) as THREE.Color).getHexString();
	}

	onFadeInEffect(factor : number)
	{
		this.fadeOpacity = factor;
	}

	// TODO: COLOR RANGE: Color range stuff. 
	colorRange: Record<number, number> | null = null;
	stripColorsAndGetIndexes(input: string)
	{
		const pattern = /<color=([^>]+)>(.*?)<\/color>/gi;
		const spans: Array<{start: number, color: number}> = [];
		let cleaned = '';
		let lastIndex = 0;
		const shift = 0;
	
		let match: RegExpExecArray | null;
		spans.push({
			start: 0,
			color: new THREE.Color(this.getTextColor()).getHex()
		});
		const lastEnd = -1;
		while ((match = pattern.exec(input))) 
		{
			const [fullMatch, color, content] = match;
			const matchStart = match.index;
			const matchEnd = pattern.lastIndex;
	
			// Append text before match
			cleaned += input.slice(lastIndex, matchStart);
			const insertPos = cleaned.length;
	
			// Append stripped content
			cleaned += content;

			spans.push({
				start: insertPos,
				color: new THREE.Color(color).getHex()
			});

			spans.push({
				start: matchEnd,
				color: new THREE.Color(this.getTextColor()).getHex()
			});
	
			lastIndex = matchEnd;
		}
	
		// Append the remaining text
		cleaned += input.slice(lastIndex);

		if (spans.length > 0)
		{
			const map:Record<number, number> = spans.sort((a, b) => a.start - b.start).reduce((acc, span) => ({
				...acc,
				[span.start]: span.color
			}), {});
			this.colorRange = map;
			console.log(map);
		}
		else
		{
			this.colorRange = null;
		}
	
		return cleaned;
	}
}

export default TroikaText3D;